import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, ArrowRight, Repeat, Zap, Landmark, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ONE_OFF_PRESETS = [10, 25, 50, 100, 250];
const MONTHLY_PRESETS = [10, 25, 50, 200];
const BENEMERITO_THRESHOLD = 200; // sync with quotas config default

export default function Support() {
  const [frequency, setFrequency] = useState("one_off"); // 'one_off' | 'monthly'
  const [amount, setAmount] = useState(25);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", company: "", message: "",
    billing_nif: "", billing_address: "", billing_company_nif: "",
  });
  const [loading, setLoading] = useState(false);

  const presets = frequency === "monthly" ? MONTHLY_PRESETS : ONE_OFF_PRESETS;
  const finalAmount = custom ? parseFloat(custom) : amount;
  const benemerito = finalAmount >= BENEMERITO_THRESHOLD;

  const submit = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount <= 0) { toast.error("Valor inválido."); return; }
    setLoading(true);
    try {
      const endpoint = frequency === "monthly"
        ? `${API}/donations/pledge/checkout`
        : `${API}/donations/checkout`;
      const payload = {
        ...form,
        amount_eur: finalAmount,
        language: "pt",
        origin_url: window.location.origin,
      };
      if (frequency === "monthly") payload.frequency = "monthly";
      const r = await axios.post(endpoint, payload);
      if (r.data.url) window.location.href = r.data.url;
    } catch (err) {
      toast.error("Erro a criar pagamento.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="support-page">
      <section className="bg-[hsl(var(--primary))] text-white py-20 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="container-narrow relative text-center">
          <Heart className="h-12 w-12 text-[hsl(var(--accent))] mx-auto" />
          <div className="overline text-white/80 mt-6">Apoiar o CIEES</div>
          <h1 className="display-heading text-5xl sm:text-6xl mt-5 leading-[1.05]">Apoiar a missão.</h1>
          <span className="accent-rule mt-8 inline-block" />
          <p className="mt-8 text-lg text-white/85 leading-relaxed">
            Para desenvolver e implementar a pedagogia do Pe. Kentenich no mundo do trabalho, em coordenação com o Movimento de Schoenstatt.
          </p>
        </div>
      </section>

      <section className="container-narrow py-16">
        <form onSubmit={submit} className="bg-white border border-[hsl(var(--border))] p-8 sm:p-10 space-y-6" data-testid="support-form">
          {/* Frequency toggle */}
          <div>
            <div className="overline">Frequência</div>
            <div className="mt-5 grid grid-cols-2 gap-3" role="tablist">
              <button
                type="button"
                onClick={() => { setFrequency("one_off"); setCustom(""); setAmount(25); }}
                className={`flex items-center justify-center gap-2 py-4 text-sm uppercase tracking-[0.18em] font-medium border-2 transition-all ${frequency === "one_off" ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-white text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]"}`}
                data-testid="support-frequency-oneoff"
              >
                <Zap className="h-4 w-4" /> Único
              </button>
              <button
                type="button"
                onClick={() => { setFrequency("monthly"); setCustom(""); setAmount(25); }}
                className={`flex items-center justify-center gap-2 py-4 text-sm uppercase tracking-[0.18em] font-medium border-2 transition-all ${frequency === "monthly" ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-white text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]"}`}
                data-testid="support-frequency-monthly"
              >
                <Repeat className="h-4 w-4" /> Mensal
              </button>
            </div>
            {frequency === "monthly" && (
              <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed" data-testid="support-monthly-hint">
                O primeiro pagamento é feito hoje. Todos os meses receberá um email com um link único para concluir o donativo seguinte — pode cancelar a qualquer momento.
              </p>
            )}
          </div>

          <div className="border-t border-[hsl(var(--border))] pt-6">
            <div className="overline">{frequency === "monthly" ? "Valor mensal" : "Valor"}</div>
            <div className="mt-5 grid grid-cols-3 sm:grid-cols-5 gap-3">
              {presets.map((v) => (
                <button
                  type="button"
                  key={v}
                  onClick={() => { setAmount(v); setCustom(""); }}
                  className={`py-3 text-sm font-medium border transition-colors ${!custom && amount === v ? "bg-[hsl(var(--accent))] text-[#1A201F] border-[hsl(var(--accent))]" : "bg-white text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]"}`}
                  data-testid={`support-amount-${v}`}
                >{v} €{frequency === "monthly" ? "/mês" : ""}</button>
              ))}
            </div>
            <div className="mt-4">
              <input
                type="number" min="1" step="1" placeholder={`Outro valor (€${frequency === "monthly" ? "/mês" : ""})`}
                value={custom} onChange={(e) => setCustom(e.target.value)}
                className="w-full bg-white border border-[hsl(var(--border))] px-4 py-3 focus:outline-none focus:border-[hsl(var(--accent))]"
                data-testid="support-custom-amount"
              />
            </div>
            {benemerito && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs" data-testid="support-benemerito-badge">
                ★ A partir de 200 € será automaticamente associado como <strong>Sócio Benemérito</strong>.
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4 border-t border-[hsl(var(--border))] pt-6">
            <Field label="Nome completo *"><input required value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} className="i" data-testid="support-name" /></Field>
            <Field label="Email *"><input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="i" data-testid="support-email" /></Field>
            <Field label="Telefone"><input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="i" data-testid="support-phone" /></Field>
            <Field label="Empresa"><input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} className="i" data-testid="support-company" /></Field>
          </div>

          <Field label="Mensagem (opcional)">
            <textarea rows={3} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className="i resize-none" data-testid="support-message" />
          </Field>

          <details className="border-t border-[hsl(var(--border))] pt-5 group" data-testid="support-billing-section">
            <summary className="cursor-pointer list-none flex items-center justify-between text-sm text-[hsl(var(--primary))] font-medium">
              <span>Dados para Declaração de Donativo <span className="text-[hsl(var(--muted-foreground))]">(opcional, para contabilidade)</span></span>
              <span className="text-xs transition-transform group-open:rotate-180">▼</span>
            </summary>
            <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">A Associação CIEES Portugal não emite faturas. Receberá um PDF de Declaração de Donativo por email.</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <Field label="NIF"><input value={form.billing_nif} onChange={(e) => setForm({...form, billing_nif: e.target.value})} className="i" data-testid="support-billing-nif" /></Field>
              <Field label="NIF da empresa"><input value={form.billing_company_nif} onChange={(e) => setForm({...form, billing_company_nif: e.target.value})} className="i" data-testid="support-billing-company-nif" /></Field>
            </div>
            <div className="mt-4">
              <Field label="Morada fiscal"><input value={form.billing_address} onChange={(e) => setForm({...form, billing_address: e.target.value})} className="i" data-testid="support-billing-address" /></Field>
            </div>
          </details>

          <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[hsl(var(--accent))] text-[#1A201F] text-sm uppercase tracking-[0.22em] font-semibold hover:bg-[#B3922E] transition-colors disabled:opacity-60" data-testid="support-submit">
            {loading ? "A processar…" : `Apoiar com ${finalAmount ? finalAmount.toFixed(2) + (frequency === "monthly" ? " €/mês" : " €") : "..."}`} <ArrowRight className="h-4 w-4" />
          </button>
          <p className="text-xs text-center text-[hsl(var(--muted-foreground))]">Pagamento seguro via Stripe · Cartão · Apple Pay · Google Pay · MB Way</p>
        </form>

        <BankTransferSection />
      </section>

      <style>{`.i { width:100%; background:white; border:1px solid hsl(var(--border)); padding:10px 12px; font-family:"Outfit", sans-serif; font-size:15px; } .i:focus { outline:none; border-color:hsl(var(--accent)); }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-2">{label}</label>
      {children}
    </div>
  );
}

// Alternative to Stripe: SEPA bank transfer details with copy buttons.
function BankTransferSection() {
  const [bank, setBank] = useState(null);
  const [copiedKey, setCopiedKey] = useState("");

  useEffect(() => {
    axios.get(`${API}/payments/bank-details`)
      .then((r) => setBank(r.data))
      .catch((e) => console.warn("[Support] bank-details:", e?.message));
  }, []);

  if (!bank?.iban) return null;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 2000);
      toast.success("Copiado.");
    } catch { toast.error("Não foi possível copiar."); }
  };

  const rows = [
    { key: "iban", label: "IBAN", value: bank.iban },
    ...(bank.bic && bank.bic !== "—" ? [{ key: "bic", label: "BIC/SWIFT", value: bank.bic }] : []),
    { key: "benef", label: "Beneficiário", value: bank.beneficiary },
  ];

  return (
    <div className="mt-8 bg-white border border-[hsl(var(--border))] p-8 sm:p-10" data-testid="support-bank-section">
      <div className="flex items-center gap-3">
        <Landmark className="h-5 w-5 text-[hsl(var(--accent))]" />
        <div className="overline">Prefere transferência bancária?</div>
      </div>
      <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed max-w-2xl">
        Pode apoiar diretamente por transferência SEPA para a conta da Associação:
      </p>
      <div className="mt-5 max-w-xl">
        {rows.map((r) => (
          <div key={r.key} className="grid grid-cols-[110px_1fr_auto] items-center gap-3 py-2.5 border-b border-[hsl(var(--border))] last:border-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{r.label}</div>
            <div className="font-mono text-[13px] text-[hsl(var(--foreground))] break-all" data-testid={`support-bank-${r.key}`}>{r.value}</div>
            <button
              type="button"
              onClick={() => copy(r.value, r.key)}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--accent))]"
              data-testid={`support-bank-copy-${r.key}`}
            >
              <Copy className="h-3 w-3" /> {copiedKey === r.key ? "✓" : "Copiar"}
            </button>
          </div>
        ))}
      </div>
      <p className="mt-5 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed max-w-2xl">
        Após transferir, envie o comprovativo para{" "}
        <a href="mailto:geral@ciees.pt" className="text-[hsl(var(--primary))] underline">geral@ciees.pt</a>{" "}
        com o seu nome e NIF (se desejar a Declaração de Donativo para efeitos fiscais). Confirmamos a receção
        e enviamos a Declaração em PDF por email.
      </p>
    </div>
  );
}
